import {Form, Select, Input, InputNumber, DatePicker, Tooltip, Switch} from 'antd';
import {motion} from 'framer-motion';
import styles from './walletTransaction.module.scss';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {enabledPaymentMethod, paymentProviders} from 'utils/mock';
import {CloseOutlined} from '@ant-design/icons';
import {useParams} from 'react-router-dom';
import {PROVIDER_STATUS_OPTIONS} from './ProviderStatusTag';

export const TransactionsFilter = ({
  form,
  handleFilter,
  handleClose,
  handleExport,
}) => {
  const {type, provider} = useParams();
  const exportEmail = Form.useWatch('export', form);

  return (
    <motion.div
      className={styles.filterItems}
      initial={{opacity: 0, y: -20, scale: 0.95}}
      animate={{opacity: 1, y: 0, scale: 1}}
      exit={{opacity: 0, y: -20, scale: 0.95}}
      transition={{duration: 0.3, ease: 'easeOut'}}>
      <motion.div
        className={styles.filterHeader}
        initial={{opacity: 0, y: -10}}
        animate={{opacity: 1, y: 0}}
        transition={{delay: 0.1, duration: 0.3}}>
        <motion.h2>Filters</motion.h2>
        <motion.button
          type="button"
          className={styles.closeButton}
          onClick={handleClose}
          whileHover={{scale: 1.05}}
          whileTap={{scale: 0.95}}>
          <CloseOutlined
            style={{fontSize: '24px', color: '#666'}}
            onClick={handleClose}
          />
        </motion.button>
      </motion.div>
      <div>
        <Form
          form={form}
          onFinish={handleFilter}
          className={styles.form}
          layout="vertical">
          <motion.div
            className={styles.fieldPair}
            initial={{opacity: 0, x: -20}}
            animate={{opacity: 1, x: 0}}
            transition={{delay: 0.2, duration: 0.3}}>
            <Form.Item name="type" initialValue={type} label="Transaction Type">
              <Select
                disabled={type}
                allowClear
                placeholder="Select transaction type">
                <Select.Option key="ALL">All</Select.Option>
                <Select.Option key="DEPOSIT">Deposit</Select.Option>
                <Select.Option key="WITHDRAWAL">Withdrawal</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="provider" label="Provider" initialValue={provider}>
              <Select
                disabled={provider}
                allowClear
                placeholder="Select provider">
                {paymentProviders.map(provider => (
                  <Select.Option key={provider.key} value={provider.key}>
                    {provider.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </motion.div>
          <motion.div className={styles.fieldPair}>
            <Form.Item name="paymentMethod" label="Payment Method">
              <Select allowClear placeholder="Select payment method">
                {enabledPaymentMethod.map(method => (
                  <Select.Option key={method.key} value={method.key}>
                    {method.value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="status" label="Status">
              <Select allowClear placeholder="Select status">
                <Select.Option key="SUCCESSFUL">Successful</Select.Option>
                <Select.Option key="PENDING">Pending</Select.Option>
                <Select.Option key="FAILED">Failed</Select.Option>
              </Select>
            </Form.Item>
          </motion.div>
          <motion.div className={styles.fullWidthField}>
            <Form.Item
              name="providerStatus"
              label={
                <Tooltip
                  trigger="hover"
                  title="Whether the withdrawal has reached the payment provider, independent of the Daba status. Withdrawals only. Tracked for Maviance today.">
                  Provider Status
                </Tooltip>
              }>
              <Select allowClear placeholder="Select provider status">
                {PROVIDER_STATUS_OPTIONS.map(option => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </motion.div>
          <motion.div className={styles.fullWidthField}>
            <Form.Item name="direction" label="Direction">
              <Select allowClear placeholder="Select direction">
                <Select.Option key="USER_WALLET_TO_USER_BANK_ACCOUNT">
                  User Wallet to User Bank Account (Withdrawal)
                </Select.Option>
                <Select.Option key="CURRENCY_SWAP">Currency Swap</Select.Option>
                <Select.Option key="DIVIDEND_PAYOUT">
                  Dividend Payout
                </Select.Option>
              </Select>
            </Form.Item>
          </motion.div>

          <motion.div
            className={styles.fullWidthField}
            initial={{opacity: 0, y: 10}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.4, duration: 0.3}}>
            <Form.Item
              name="search"
              label={
                <div>
                  <Tooltip
                    trigger="hover"
                    title="Search by the following ID type: Transaction, Reference, Provider">
                    Search by ID
                  </Tooltip>
                </div>
              }>
              <Input placeholder="Search by ID" />
            </Form.Item>
          </motion.div>

          <motion.div
            className={styles.dateFields}
            initial={{opacity: 0, y: 10}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.5, duration: 0.3}}>
            <Form.Item name="startDate" label="Start Date">
              <DatePicker
                format={'YYYY-MM-DD'}
                allowClear
                placeholder="Start date"
              />
            </Form.Item>
            <Form.Item name="endDate" label="End Date">
              <DatePicker
                format={'YYYY-MM-DD'}
                allowClear
                placeholder="End date"
              />
            </Form.Item>
          </motion.div>

          <motion.div
            className={styles.dateFields}
            initial={{opacity: 0, y: 10}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.55, duration: 0.3}}>
            {/*
              Amount range on totalTransactionAmountIncludingFees (the
              gross amount — same column the table renders). Either bound
              is optional; sending only one is a half-open range on the
              server. Type=number so a paste of "1,000" is coerced but
              stripped of the comma before submit.
            */}
            <Form.Item name="minAmount" label="Min Amount">
              <InputNumber
                min={0}
                style={{width: '100%'}}
                placeholder="Min amount"
                formatter={value =>
                  value === undefined || value === null || value === ''
                    ? ''
                    : `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={value => (value ? value.replace(/,/g, '') : '')}
              />
            </Form.Item>
            <Form.Item
              name="maxAmount"
              label="Max Amount"
              dependencies={['minAmount']}
              rules={[
                ({getFieldValue}) => ({
                  validator(_, value) {
                    const min = getFieldValue('minAmount');
                    if (value === undefined || value === null || value === '')
                      return Promise.resolve();
                    if (min === undefined || min === null || min === '')
                      return Promise.resolve();
                    return Number(value) >= Number(min)
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error('Max amount must be ≥ min amount'),
                        );
                  },
                }),
              ]}>
              <InputNumber
                min={0}
                style={{width: '100%'}}
                placeholder="Max amount"
                formatter={value =>
                  value === undefined || value === null || value === ''
                    ? ''
                    : `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={value => (value ? value.replace(/,/g, '') : '')}
              />
            </Form.Item>
          </motion.div>

          <Form.Item
            name="automated"
            label={
              <Tooltip
                trigger="hover"
                title="Only show transactions the reconciliation automation has touched (SUCCESS_ADJUST, SUCCESS_NO_ADJUST or FAIL_DUPLICATE).">
                Automated only
              </Tooltip>
            }
            valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="export" label="Export" valuePropName="checked">
            <Switch />
          </Form.Item>
          <motion.div
            className={styles.submitArea}
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.75, duration: 0.3}}>
            {exportEmail && (
              <Form.Item
                rules={[
                  {
                    type: 'email',
                    message: 'Please enter a valid email address',
                  },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      if (!value.endsWith('@joindaba.com')) {
                        return Promise.reject(
                          new Error('Only daba emails are allowed'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                className={styles.field}
                name="adminEmail"
                tooltip="If email is not provided, it will be exported to the email that you logged in with"
                label="Email">
                <Input
                  placeholder="Enter your daba email address"
                  className={styles.smallInput}
                  type="email"
                />
              </Form.Item>
            )}
            <div className={styles.buttonGroup}>
              <SubmitButton label="Apply Filters" />
              {exportEmail && (
                <SubmitButton
                  label="Apply Filters & Export"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    form.validateFields(['adminEmail']).then(() => {
                      handleExport(form.getFieldsValue());
                    });
                  }}
                />
              )}
            </div>
          </motion.div>
        </Form>
      </div>
    </motion.div>
  );
};
